import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, formatApiErrorDetail } from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { UserCircle, Plus, X, EnvelopeSimple, WhatsappLogo, ChatCircle } from '@phosphor-icons/react';

const CHANNELS = ['email', 'whatsapp', 'sms'];
const CHANNEL_ICON = { email: EnvelopeSimple, whatsapp: WhatsappLogo, sms: ChatCircle };
const RELATIONSHIPS = ['daughter', 'son', 'spouse', 'other'];

function Picker({ users, onPick, onCancel, t, includeRelationship = false }) {
  const [userId, setUserId] = useState('');
  const [relationship, setRelationship] = useState('daughter');
  const [channels, setChannels] = useState([...CHANNELS]);

  const toggleChannel = (c) =>
    setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  return (
    <div className="border border-stone_line rounded-xl p-4 bg-bg-alt/40 space-y-3 animate-fade-up">
      <select
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        data-testid="picker-user-select"
        className="w-full px-3 py-2 rounded-lg border border-stone_line bg-white text-sm focus:outline-none focus:border-sage"
      >
        <option value="">{t('select_user')}</option>
        {users.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name} · {u.email}
          </option>
        ))}
      </select>

      {includeRelationship && (
        <>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-ink-muted font-semibold mb-2">
              {t('relationship')}
            </div>
            <div className="flex gap-2 flex-wrap">
              {RELATIONSHIPS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRelationship(r)}
                  data-testid={`picker-rel-${r}`}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                    relationship === r
                      ? 'bg-sage text-white border-sage'
                      : 'bg-white text-ink-soft border-stone_line hover:bg-bg-alt'
                  }`}
                >
                  {t(`relationship_${r}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-ink-muted font-semibold mb-2">
              {t('notify_via')}
            </div>
            <div className="flex gap-2 flex-wrap">
              {CHANNELS.map((c) => {
                const Icon = CHANNEL_ICON[c];
                const active = channels.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleChannel(c)}
                    data-testid={`picker-channel-${c}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border transition-colors ${
                      active
                        ? 'bg-sage-100 text-sage-600 border-sage/30'
                        : 'bg-white text-ink-muted border-stone_line line-through'
                    }`}
                  >
                    <Icon size={14} />
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          data-testid="picker-cancel"
          className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt transition-colors"
        >
          {t('cancel')}
        </button>
        <button
          type="button"
          disabled={!userId}
          onClick={() => onPick({ user_id: userId, relationship, notify_channels: channels })}
          data-testid="picker-confirm"
          className="px-4 py-2 rounded-lg bg-sage hover:bg-sage-600 text-white text-sm transition-colors disabled:opacity-50"
        >
          {t('add')}
        </button>
      </div>
    </div>
  );
}

function MemberRow({ member, onRemove, testid }) {
  return (
    <li
      data-testid={testid}
      className="flex items-center gap-3 p-3 rounded-xl border border-stone_line hover:bg-bg-alt/50 transition-colors"
    >
      <div className="w-9 h-9 rounded-full bg-sage-100 text-sage-600 grid place-items-center">
        <UserCircle size={20} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-ink truncate">{member.name}</div>
        <div className="text-xs text-ink-muted truncate">
          {member.email}
          {member.relationship && (
            <span className="ml-2 text-sage-600">· {member.relationship}</span>
          )}
        </div>
      </div>
      {member.notify_channels && member.notify_channels.length > 0 && (
        <div className="hidden md:flex gap-1">
          {member.notify_channels.map((c) => {
            const Icon = CHANNEL_ICON[c];
            return <Icon key={c} size={14} className="text-ink-muted" />;
          })}
        </div>
      )}
      <button
        onClick={onRemove}
        data-testid={`${testid}-remove`}
        className="p-2 rounded-lg text-ink-muted hover:text-terracotta-700 hover:bg-terracotta-100 transition-colors"
        title="remove"
      >
        <X size={16} weight="bold" />
      </button>
    </li>
  );
}

export default function AssignmentCards({ residentId }) {
  const { t } = useI18n();
  const [allUsers, setAllUsers] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [family, setFamily] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(null); // 'caregiver' | 'family' | null

  const reload = async () => {
    const [u, c, f] = await Promise.all([
      api.get('/users'),
      api.get(`/patients/${residentId}/caregivers`),
      api.get(`/patients/${residentId}/family`),
    ]);
    setAllUsers(u.data || []);
    setCaregivers(c.data || []);
    setFamily(f.data || []);
  };

  useEffect(() => {
    reload().catch(() => {});
  }, [residentId]);

  const assignedCaregiverIds = new Set(caregivers.map((c) => c.id));
  const assignedFamilyIds = new Set(family.map((f) => f.id));
  const availableCaregivers = allUsers.filter(
    (u) => (u.role === 'caregiver' || u.role === 'admin') && !assignedCaregiverIds.has(u.id)
  );
  const availableFamily = allUsers.filter(
    (u) => u.role === 'family' && !assignedFamilyIds.has(u.id)
  );

  const handleApiError = (action, e) => {
    const status = e?.response?.status;
    const detail = formatApiErrorDetail(e?.response?.data?.detail) || e?.message;
    // eslint-disable-next-line no-console
    console.error('[AssignmentCards]', action, 'failed', status, detail, e);
    if (status === 404) {
      toast.error(`${action}: endpoint not available on this backend yet (404).`);
    } else if (status === 401 || status === 403) {
      toast.error(`${action}: not authorised.`);
    } else {
      toast.error(`${action} failed${status ? ` (HTTP ${status})` : ''}: ${detail}`);
    }
  };

  const addCaregiver = async ({ user_id }) => {
    try {
      await api.post(`/patients/${residentId}/caregivers`, { user_id });
      toast.success(t('caregiver_added'));
      setPickerOpen(null);
      reload();
    } catch (e) {
      handleApiError('Add caregiver', e);
    }
  };
  const removeCaregiver = async (userId) => {
    try {
      await api.delete(`/patients/${residentId}/caregivers/${userId}`);
      toast.success(t('caregiver_removed'));
      reload();
    } catch (e) {
      handleApiError('Remove caregiver', e);
    }
  };
  const addFamily = async (payload) => {
    try {
      await api.post(`/patients/${residentId}/family`, payload);
      toast.success(t('family_added'));
      setPickerOpen(null);
      reload();
    } catch (e) {
      handleApiError('Add family member', e);
    }
  };
  const removeFamily = async (userId) => {
    try {
      await api.delete(`/patients/${residentId}/family/${userId}`);
      toast.success(t('family_removed'));
      reload();
    } catch (e) {
      handleApiError('Remove family member', e);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone_line p-8 shadow-soft" data-testid="assignment-cards">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-heading text-2xl text-ink">{t('admin_only_section')}</h2>
        <span className="text-xs uppercase tracking-[0.18em] text-sage-600 bg-sage-100 px-2.5 py-1 rounded-full">
          admin
        </span>
      </div>
      <p className="text-sm text-ink-soft mb-6">
        Manage who can see and respond to alerts for this resident.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Care team */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-ink uppercase tracking-[0.18em]">
              {t('care_team')}
            </h3>
            {pickerOpen !== 'caregiver' && (
              <button
                onClick={() => setPickerOpen('caregiver')}
                data-testid="add-caregiver-button"
                className="inline-flex items-center gap-1 text-xs text-sage hover:text-sage-600"
              >
                <Plus size={14} weight="bold" />
                {t('add_caregiver')}
              </button>
            )}
          </div>
          {pickerOpen === 'caregiver' && (
            <div className="mb-3">
              <Picker
                users={availableCaregivers}
                onPick={addCaregiver}
                onCancel={() => setPickerOpen(null)}
                t={t}
              />
            </div>
          )}
          {caregivers.length === 0 ? (
            <div className="text-sm text-ink-muted bg-bg-alt rounded-xl p-4">
              {t('no_caregivers')}
            </div>
          ) : (
            <ul className="space-y-2" data-testid="caregivers-list">
              {caregivers.map((c) => (
                <MemberRow
                  key={c.id}
                  member={c}
                  onRemove={() => removeCaregiver(c.id)}
                  testid={`caregiver-${c.id}`}
                />
              ))}
            </ul>
          )}
        </section>

        {/* Family */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-ink uppercase tracking-[0.18em]">
              {t('family_members')}
            </h3>
            {pickerOpen !== 'family' && (
              <button
                onClick={() => setPickerOpen('family')}
                data-testid="add-family-button"
                className="inline-flex items-center gap-1 text-xs text-sage hover:text-sage-600"
              >
                <Plus size={14} weight="bold" />
                {t('add_family')}
              </button>
            )}
          </div>
          {pickerOpen === 'family' && (
            <div className="mb-3">
              <Picker
                users={availableFamily}
                onPick={addFamily}
                onCancel={() => setPickerOpen(null)}
                t={t}
                includeRelationship
              />
            </div>
          )}
          {family.length === 0 ? (
            <div className="text-sm text-ink-muted bg-bg-alt rounded-xl p-4">
              {t('no_family')}
            </div>
          ) : (
            <ul className="space-y-2" data-testid="family-list">
              {family.map((f) => (
                <MemberRow
                  key={f.id}
                  member={f}
                  onRemove={() => removeFamily(f.id)}
                  testid={`family-${f.id}`}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
